import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Paperclip, CaretDown, CaretRight, Plus } from "@phosphor-icons/react";

/**
 * AIContextPicker — Iter 85.16 · shared pre-flight context picker used
 * by both the Briefing Studio Review flow (client_briefing /
 * engagement_briefing) and the meeting-note Organize flow. Reads the
 * same `GET /notes/{id}/briefing/extract-context` endpoint so both AI
 * actions see attachments and sibling notes identically.
 *
 * Iter 85.30 · The picker now hosts its own inline upload affordance
 * (button + drag/drop) so partners can attach source documents without
 * leaving the picker. Previously the picker was purely read-only,
 * which meant users had to open the full NoteViewerDialog to upload
 * before returning to the picker — a confusing round-trip. The AI
 * organizer / briefing extractor already ingest text-extractable
 * attachments picked here (`context.attachment_ids` threaded through
 * `/notes/{id}/organize` and `/notes/{id}/briefing/extract`), so
 * upload → tick → run stays a single-surface flow.
 *
 * Props:
 *   noteId              — the note whose context to load
 *   pickedAttachments   — Set<string> of selected attachment ids
 *   setPickedAttachments — setter (accepts Set)
 *   pickedPriorNotes    — Set<string> of selected prior-note ids
 *   setPickedPriorNotes — setter (accepts Set)
 *   testIdPrefix        — testid namespace, e.g. "briefing-context" or "organize-context"
 *   onAttachmentUploaded — optional (attachment) => void callback so
 *                          parents can refresh their local note state
 *                          after an inline upload.
 *
 * Renders as a collapsed disclosure. Callers own the picked-state so the
 * eventual Organize / Extract call reads it directly.
 */
export default function AIContextPicker({
  noteId,
  pickedAttachments,
  setPickedAttachments,
  pickedPriorNotes,
  setPickedPriorNotes,
  testIdPrefix = "ai-context",
  onAttachmentUploaded,
}) {
  const [ctx, setCtx] = useState(null);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const refreshCtx = async () => {
    if (!noteId) return;
    try {
      const { data } = await api.get(`/notes/${noteId}/briefing/extract-context`);
      setCtx(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not refresh context");
    }
  };

  // Lazy-load. Cached in `ctx` for the rest of this dialog session; if
  // the partner uploads a new attachment mid-session the picker
  // re-fetches automatically after the upload finishes.
  useEffect(() => {
    if (!open || ctx || !noteId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/notes/${noteId}/briefing/extract-context`);
        if (!cancelled) setCtx(data);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Could not load context");
      }
    })();
    return () => { cancelled = true; };
  }, [open, ctx, noteId]);

  const uploadFile = async (file) => {
    if (!file || !noteId) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File too large (max 25 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/notes/${noteId}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Attached ${data.filename}`);
      // Auto-tick the fresh upload if it's text-extractable so the AI
      // organizer picks it up on the next run without an extra click.
      if (data.text_extractable) {
        setPickedAttachments((s) => new Set(s).add(data.id));
      }
      onAttachmentUploaded?.(data);
      await refreshCtx();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onFileInputChange = async (ev) => {
    const file = ev.target.files?.[0];
    await uploadFile(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onDrop = async (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    setDragOver(false);
    const file = ev.dataTransfer?.files?.[0];
    if (file) await uploadFile(file);
  };
  const onDragOver = (ev) => { ev.preventDefault(); ev.stopPropagation(); setDragOver(true); };
  const onDragLeave = (ev) => { ev.preventDefault(); ev.stopPropagation(); setDragOver(false); };

  return (
    <div className="border border-border bg-secondary/30" data-testid={testIdPrefix}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-secondary/50"
        data-testid={`${testIdPrefix}-toggle`}
      >
        <div className="flex items-center gap-2 text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
          {open ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
          <Paperclip size={11} weight="bold" />
          Extraction context
          {(pickedAttachments.size || pickedPriorNotes.size) ? (
            <span className="text-brass">
              · {pickedAttachments.size} attachment{pickedAttachments.size === 1 ? "" : "s"} · {pickedPriorNotes.size} prior note{pickedPriorNotes.size === 1 ? "" : "s"}
            </span>
          ) : (
            <span className="italic normal-case tracking-normal font-sans text-muted-foreground/80">
              transcript + written body only
            </span>
          )}
        </div>
      </button>
      {open && (
        <div className="border-t border-border px-3 py-3 space-y-4" data-testid={`${testIdPrefix}-body`}>
          {!ctx ? (
            <div className="text-xs text-muted-foreground italic">Loading context candidates…</div>
          ) : (
            <>
              <section
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                className={dragOver ? "outline-dashed outline-2 outline-offset-2 outline-primary/50 bg-primary/5" : ""}
              >
                <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary mb-1.5 flex items-center gap-1.5 justify-between">
                  <span className="flex items-center gap-1.5">
                    <Paperclip size={10} weight="bold" /> Attachments ({ctx.attachments.length})
                  </span>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="flex items-center gap-1 px-1.5 py-0.5 border border-border hover:bg-secondary/70 disabled:opacity-50 disabled:cursor-not-allowed text-[0.625rem] font-mono tracking-widest text-primary"
                    data-testid={`${testIdPrefix}-attach-btn`}
                  >
                    <Plus size={10} weight="bold" /> {uploading ? "Uploading…" : "Attach file"}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={onFileInputChange}
                    className="hidden"
                    data-testid={`${testIdPrefix}-attach-input`}
                  />
                </div>
                {ctx.attachments.length === 0 ? (
                  <div className="text-xs text-muted-foreground italic">
                    No files attached to this note. Drop a source document here or click <b>Attach file</b> above.
                    {" "}The AI organizer will read every ticked, text-extractable file when you run Organize.
                  </div>
                ) : (
                  <ul className="space-y-1">
                    {ctx.attachments.map((a) => {
                      const canPick = a.text_extractable;
                      const picked = pickedAttachments.has(a.id);
                      return (
                        <li key={a.id} className="text-xs" data-testid={`${testIdPrefix}-attachment-${a.id}`}>
                          <label className={`flex items-center gap-2 ${canPick ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}>
                            <input
                              type="checkbox"
                              disabled={!canPick}
                              checked={picked}
                              onChange={() =>
                                setPickedAttachments((s) => {
                                  const n = new Set(s);
                                  if (n.has(a.id)) n.delete(a.id); else n.add(a.id);
                                  return n;
                                })
                              }
                              data-testid={`${testIdPrefix}-attachment-toggle-${a.id}`}
                            />
                            <span className="truncate flex-1">{a.filename}</span>
                            <span className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground shrink-0">
                              {canPick ? (a.content_type?.split("/")[1] || "text") : "not text-extractable"}
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
              <section>
                <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary mb-1.5">
                  Prior notes on this client / engagement ({ctx.prior_notes.length})
                </div>
                {ctx.prior_notes.length === 0 ? (
                  <div className="text-xs text-muted-foreground italic">
                    No sibling notes exist yet on this client or engagement.
                  </div>
                ) : (
                  <ul className="space-y-1">
                    {ctx.prior_notes.map((n) => {
                      const canPick = n.usable;
                      const picked = pickedPriorNotes.has(n.id);
                      return (
                        <li key={n.id} className="text-xs" data-testid={`${testIdPrefix}-priornote-${n.id}`}>
                          <label className={`flex items-start gap-2 ${canPick ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}>
                            <input
                              type="checkbox"
                              disabled={!canPick}
                              checked={picked}
                              onChange={() =>
                                setPickedPriorNotes((s) => {
                                  const set = new Set(s);
                                  if (set.has(n.id)) set.delete(n.id); else set.add(n.id);
                                  return set;
                                })
                              }
                              className="mt-0.5"
                              data-testid={`${testIdPrefix}-priornote-toggle-${n.id}`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="truncate">{n.title}</div>
                              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
                                {n.note_type} · {n.date || "no date"}
                                {" · "}
                                {n.has_transcript ? "transcript" : n.has_body ? "body" : n.has_summary ? "summary only" : "no text"}
                              </div>
                            </div>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
              {(pickedAttachments.size || pickedPriorNotes.size) ? (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => { setPickedAttachments(new Set()); setPickedPriorNotes(new Set()); }}
                    className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-destructive"
                    data-testid={`${testIdPrefix}-clear`}
                  >
                    Clear selection
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>
      )}
    </div>
  );
}
